import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../../../config/axiosConfig';
import useFetch from '../../../../hooks/useFetch';
import { API } from '../../../../utils/apiRoutes';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { formatJalaliDateTime } from '../../../../utils/formatJalaliDate';
import { NEW_KEY, useAssistant } from '../../../../context/AssistantContext';

// «خلاصهٔ امروز» — the manager's AI daily summary (manager-only endpoint; the
// overview renders this card only when the server reports role=manager).
// The numbers live in the overview tiles, so this card holds just the text:
// short by default, expandable, with the report date and generation time.
function DailyBriefingCard() {
    const theme = useTheme();
    const assistant = useAssistant();
    const { data: fetched, loading, error, refetch } = useFetch(API.assistantBriefing);
    const [refreshed, setRefreshed] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState('');
    const [expanded, setExpanded] = useState(false);
    const data = refreshed || fetched;

    // The briefing is cached server-side for 30 minutes; this bypasses it.
    const handleRefresh = async () => {
        setRefreshing(true);
        setRefreshError('');
        try {
            const response = await api.get(API.assistantBriefing, { params: { refresh: 'true' } });
            setRefreshed(response.data);
        } catch {
            setRefreshError('به‌روزرسانی خلاصه انجام نشد؛ نسخهٔ قبلی نمایش داده می‌شود.');
        } finally {
            setRefreshing(false);
        }
    };

    // Puts a question about this report into the assistant's draft — the
    // user reviews/completes it and sends it themselves.
    const askAboutReport = () => {
        if (!assistant?.enabled || !data) return;
        assistant.setDraft(NEW_KEY, `دربارهٔ گزارش روزانهٔ ${data.report_date_jalali}: `);
        assistant.openNew();
    };

    const cardSx = {
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.border.main, 0.35)}`,
        backgroundImage: 'none',
    };

    if (loading && !data) {
        return (
            <Paper elevation={0} sx={{ ...cardSx, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} aria-label="در حال دریافت خلاصهٔ امروز" />
            </Paper>
        );
    }

    if (error && !data) {
        return (
            <Paper elevation={0} sx={cardSx}>
                <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={() => refetch().catch(() => {})}>تلاش دوباره</Button>}
                >
                    خلاصهٔ امروز دریافت نشد.
                </Alert>
            </Paper>
        );
    }

    if (!data) return null;
    const attention = data.attention || [];
    const summary = data.summary ? toPersianDigits(data.summary) : '';
    const long = summary.length > 280 || attention.length > 0;

    return (
        <Paper elevation={0} sx={cardSx} component="section" aria-labelledby="briefing-title">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AutoAwesomeOutlinedIcon fontSize="small" color="primary" />
                <Typography id="briefing-title" component="h2" sx={{ fontWeight: 700, fontSize: '1rem', flex: 1 }}>
                    خلاصهٔ امروز
                </Typography>
                <Tooltip title="ساخت دوبارهٔ خلاصه">
                    <span>
                        <IconButton size="small" disabled={refreshing} onClick={handleRefresh} aria-label="ساخت دوبارهٔ خلاصهٔ امروز">
                            {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="inherit" />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {toPersianDigits(
                    `گزارش ${data.report_date_jalali} · ساخته‌شده ${formatJalaliDateTime(data.generated_at)}${data.cached ? ' (ذخیره‌شده)' : ''}`,
                )}
            </Typography>

            {summary ? (
                <Typography
                    variant="body2"
                    sx={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.9,
                        ...(expanded
                            ? {}
                            : { display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
                    }}
                >
                    {summary}
                </Typography>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    {data.ai_available
                        ? 'خلاصه‌ای ساخته نشد؛ عددهای بالا از داده‌های واقعی‌اند.'
                        : 'خلاصهٔ هوشمند فعال نیست؛ عددهای بالا از داده‌های واقعی‌اند.'}
                </Typography>
            )}

            {expanded && attention.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                        نکته‌های خلاصهٔ هوشمند
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pr: 2.5, pl: 0 }}>
                        {attention.map((item) => (
                            <Typography key={item} component="li" variant="body2" sx={{ mb: 0.25 }}>
                                {toPersianDigits(item)}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}

            {refreshError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }} role="status">
                    {refreshError}
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                {long && summary && (
                    <Button size="small" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
                        {expanded ? 'نمایش کوتاه' : 'ادامهٔ خلاصه'}
                    </Button>
                )}
                {assistant?.enabled && (
                    <Button size="small" variant="outlined" onClick={askAboutReport}>
                        دربارهٔ این گزارش بپرس
                    </Button>
                )}
            </Box>
        </Paper>
    );
}

export default DailyBriefingCard;
